'use client';

import { useState, useRef, useEffect } from 'react';
import { Download, Copy, Check, RefreshCw, Link as LinkIcon, Type, Image as ImageIcon } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { qrCodeGeneratorContent } from '../../data/qr-code-generator-content';
import Link from 'next/link';

export default function QRCodeGenerator() {
  // Input state
  const [text, setText] = useState('https://example.com');
  const [qrValue, setQrValue] = useState('https://example.com');

  // QR Code settings
  const [size, setSize] = useState(256);
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [fgColor, setFgColor] = useState('#000000');
  const [level, setLevel] = useState('H'); // L, M, Q, H
  const [includeMargin, setIncludeMargin] = useState(false);
  const [copied, setCopied] = useState(false);

  // Refs
  const qrRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Update QR code when settings change
  useEffect(() => {
    const timer = setTimeout(() => {
      setQrValue(text);
    }, 500);

    return () => clearTimeout(timer);
  }, [text]);

  // Copy text to clipboard
  const copyToClipboard = (text: string) => {
    if (!text) return;

    // Fallback for browsers that don't support the Clipboard API
    if (!navigator.clipboard) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toast.success('Copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
        toast.error('Failed to copy to clipboard');
      }
      document.body.removeChild(textArea);
      return;
    }

    // Modern Clipboard API
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy to clipboard');
    });
  };

  // Download QR code as PNG
  const downloadPNG = () => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    const svgData = new XMLSerializer().serializeToString(svg);
    const url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));

    img.onload = () => {
      canvas.width = size;
      canvas.height = size;
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, size, size);

      const png = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'qrcode.png';
      link.href = png;
      link.click();
    };

    img.src = url;
  };

  // Download QR code as SVG
  const downloadSVG = () => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const link = document.createElement('a');
    link.download = 'qrcode.svg';
    link.href = url;
    link.click();

    URL.revokeObjectURL(url);
  };

  // Insert sample text
  const insertSample = (type: 'url' | 'text' | 'wifi' | 'contact') => {
    const samples = {
      url: 'https://example.com',
      text: 'Hello, World! This is a sample QR code text.',
      wifi: 'WIFI:T:WPA;S:MyWiFi;P:password123;;',
      contact: 'BEGIN:VCARD\nVERSION:3.0\nN:Last;First\nTEL:1234567890\nEMAIL:email@example.com\nEND:VCARD'
    };

    setText(samples[type]);
    toast.success('Sample inserted!');
  };

  // Clear all fields
  const clearAll = () => {
    setText('');
    setSize(256);
    setBgColor('#FFFFFF');
    setFgColor('#000000');
    setLevel('H');
    setIncludeMargin(false);
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="mb-8">
        <ToolPageIntro title="QR Code Generator">
                Generate custom QR codes for free with our online QR code generator. Create QR codes for URLs, text, WiFi, and more with customizable colors and sizes.
            </ToolPageIntro>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Input Section */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Content</h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="qr-text" className="block text-sm font-medium text-gray-700 mb-1">
                  Text or URL
                </label>
                <div className="flex rounded-md shadow-sm">
                  <textarea
                    id="qr-text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="flex-1 min-h-24 block w-full px-3 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter text or URL..."
                    spellCheck="false"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Quick Insert</label>
                  <button
                    onClick={clearAll}
                    className="text-xs text-gray-600 hover:text-gray-900"
                  >
                    Clear All
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => insertSample('url')}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <LinkIcon className="h-4 w-4 mr-1.5" />
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => insertSample('text')}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Type className="h-4 w-4 mr-1.5" />
                    Text
                  </button>
                  <button
                    type="button"
                    onClick={() => insertSample('wifi')}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904 3.905 10.236 3.905 14.142 0M1.394 9.393c5.858-5.857 15.355-5.857 21.213 0" />
                    </svg>
                    WiFi
                  </button>
                  <button
                    type="button"
                    onClick={() => insertSample('contact')}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Contact
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Customization</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1">
                    Size: {size}px
                  </label>
                  <input
                    id="size"
                    type="range"
                    min="100"
                    max="1000"
                    step="10"
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div>
                  <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-1">
                    Error Correction
                  </label>
                  <select
                    id="level"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="L">Low (7%)</option>
                    <option value="M">Medium (15%)</option>
                    <option value="Q">Quartile (25%)</option>
                    <option value="H">High (30%)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fg-color" className="block text-sm font-medium text-gray-700 mb-1">
                    QR Code Color
                  </label>
                  <div className="flex rounded-md shadow-sm">
                    <input
                      id="fg-color"
                      type="color"
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value)}
                      className="h-10 w-10 p-1 border border-gray-300 rounded-l-md"
                    />
                    <input
                      type="text"
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value)}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-l-0 border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="#000000"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="bg-color" className="block text-sm font-medium text-gray-700 mb-1">
                    Background Color
                  </label>
                  <div className="flex rounded-md shadow-sm">
                    <input
                      id="bg-color"
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="h-10 w-10 p-1 border border-gray-300 rounded-l-md"
                    />
                    <input
                      type="text"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-l-0 border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="#FFFFFF"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="include-margin"
                  type="checkbox"
                  checked={includeMargin}
                  onChange={(e) => setIncludeMargin(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="include-margin" className="ml-2 block text-sm text-gray-700">
                  Include margin
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Preview</h2>
              <div className="flex space-x-2">
                <button
                  onClick={downloadPNG}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  PNG
                </button>
                <button
                  onClick={downloadSVG}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  SVG
                </button>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              {qrValue ? (
                <div ref={qrRef} className="p-4 bg-white rounded-lg">
                  <QRCodeSVG
                    value={qrValue}
                    size={Math.min(300, size)}
                    bgColor={bgColor}
                    fgColor={fgColor}
                    level={level as 'L' | 'M' | 'Q' | 'H'}
                    includeMargin={includeMargin}
                  />
                </div>
              ) : (
                <div className="text-center py-8">
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No QR Code Generated</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Enter some text or a URL to generate a QR code.
                  </p>
                </div>
              )}

              {qrValue && (
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600 mb-2 break-all max-w-xs">
                    {qrValue.length > 50 ? `${qrValue.substring(0, 50)}...` : qrValue}
                  </p>
                  <button
                    onClick={() => copyToClipboard(qrValue)}
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                    title="Copy text"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 mr-1 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4 mr-1" />
                    )}
                    {copied ? 'Copied!' : 'Copy Text'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h3 className="font-medium text-blue-800 mb-2">How to use the QR Code Generator</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-blue-700 mb-1">Basic Usage:</h4>
                <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
                  <li>Enter text or a URL in the input field</li>
                  <li>Customize the QR code appearance</li>
                  <li>Download as PNG or SVG</li>
                  <li>Scan the QR code with any smartphone camera</li>
                </ol>
              </div>
              <div>
                <h4 className="font-medium text-blue-700 mb-1">Tips:</h4>
                <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                  <li>Use higher error correction for better scanning</li>
                  <li>For small QR codes, use less data</li>
                  <li>Test your QR code before printing</li>
                  <li>Use high contrast colors for better readability</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informational Content */}
      <div className="mt-12 max-w-4xl mx-auto">
        <InformationalContent
          title={qrCodeGeneratorContent.title}
          description={qrCodeGeneratorContent.description}
          content={qrCodeGeneratorContent.content}
          faqs={qrCodeGeneratorContent.faqs}
          relatedTools={qrCodeGeneratorContent.relatedTools}
        />
      </div>
    </div>
  );
}
